import {html, type TemplateResult} from 'lit';
import type {Forecast, WeatherData} from '../weather';

/**
 * Mobile pager (details page 7 days)
 * @param {Object} data
 * @return {TemplateResult}
 */
const weatherPagerDetailsMobile = (data: WeatherData): TemplateResult => html`
  <div class="pagination mobile days" data-mod-name="pagination" data-mod-nojs>
    <span class="pager icon icon-arrow-left inactive"></span>
    <span class="pager more">
      ${data.details.forecasts.map((w: Forecast, idx: number) => html`
        <span class="${idx === 0 ? 'active' : 'inactive'}">${w.label}</span>
      `)}
    </span>
    <span class="pager icon icon-arrow-right"></span>
  </div>
`;

/**
 * Weather details mobile view
 * @param {Object} data
 * @return {TemplateResult}
 */
const weatherDetailsMobile = (data: WeatherData): TemplateResult => {
  const current = data.details.forecasts[data.day];
  return html`
    <table class="header-weather">
      <tr>
        <td rowspan="3" class="current">
          <div class="max">
            <div title="${current.forecastDailyAverage.altText}"
                 class="icon-weather wi-${current.forecastDailyAverage.icon}"></div>
            <span class="label">Max.</span>
            <span class="data">${current.forecastDailyAverage.maxTemperature}&nbsp;&deg;C</span>
          </div>
        </td>
        <td class="data">${current.forecastDailyAverage.minTemperature}&nbsp;&deg;C</td>
        <td class="label">Min. Temp.</td>
      </tr>
      <tr>
        <td class="data">${current.forecastDailyAverage.precipitation}&nbsp;%</td>
        <td class="label">Niederschlag</td>
      </tr>
      <tr>
        <td class="data">${current.forecastDailyAverage.wind}&nbsp;km/h</td>
        <td class="label">Wind</td>
      </tr>
    </table>
    ${current.hasDetailedForecasts ? html`
      <table class="weather-mobile">
        <tr>
          <th>Morgens</th>
          <th>Mittags</th>
          <th>Abends</th>
          <th>Nachts</th>
        </tr>
        <tr>
          <td>
            <div title="${current.forecastMorning.altText}"
                 class="icon-weather wi-${current.forecastMorning.icon}"></div>
            <span class="data">${current.forecastMorning.temperature}&nbsp;&deg;C</span>
          </td>
          <td>
            <div title="${current.forecastNoon.altText}"
                 class="icon-weather wi-${current.forecastNoon.icon}"></div>
            <span class="data">${current.forecastNoon.temperature}&nbsp;&deg;C</span>
          </td>
          <td>
            <div title="${current.forecastEvening.altText}"
                 class="icon-weather wi-${current.forecastEvening.icon}"></div>
            <span class="data">${current.forecastEvening.temperature}&nbsp;&deg;C</span>
          </td>
          <td>
            <div title="${current.forecastNight.altText}"
                 class="icon-weather wi-night-${current.forecastNight.icon}"></div>
            <span class="data">${current.forecastNight.temperature}&nbsp;&deg;C</span>
          </td>
        </tr>
        <tr>
          <th colspan="4">Niederschlag</th>
        </tr>
        <tr>
          <td><span class="data">${current.forecastMorning.precipitation}&nbsp;%</span></td>
          <td><span class="data">${current.forecastNoon.precipitation}&nbsp;%</span></td>
          <td><span class="data">${current.forecastEvening.precipitation}&nbsp;%</span></td>
          <td><span class="data">${current.forecastNight.precipitation}&nbsp;%</span></td>
        </tr>
        <tr>
          <th colspan="4">Wind</th>
        </tr>
        <tr>
          <td>
            <span title="Aus ${current.forecastMorning.windDirection}"
                  class="icon icon-wind-${current.forecastMorning.windCode}"></span>
            <span class="data">${current.forecastMorning.wind}&nbsp;km/h</span>
          </td>
          <td>
            <span title="Aus ${current.forecastNoon.windDirection}"
                  class="icon icon-wind-${current.forecastNoon.windCode}"></span>
            <span class="data">${current.forecastNoon.wind}&nbsp;km/h</span>
          </td>
          <td>
            <span title="Aus ${current.forecastEvening.windDirection}"
                  class="icon icon-wind-${current.forecastEvening.windCode}"></span>
            <span class="data">${current.forecastEvening.wind}&nbsp;km/h</span>
          </td>
          <td>
            <span title="Aus ${current.forecastNight.windDirection}"
                  class="icon icon-wind-${current.forecastNight.windCode}"></span>
            <span class="data">${current.forecastNight.wind}&nbsp;km/h</span>
          </td>
        </tr>
      </table>
    ` : ''}
  `;
};

/**
 * City weather header tabs
 * @param {Object} data
 * @return {TemplateResult}
 */
const weatherDetailsTab = (data: WeatherData): TemplateResult => html`
  <div class="week">
    <ul>
      ${data.details.forecasts.map((w: Forecast, idx: number) => html`
        <li class="${idx === data.day ? 'active' : ''}">
          <span class="label">${w.label}</span>
          <div title="${w.forecastDailyAverage.altText}"
               class="icon-weather wi-${w.forecastDailyAverage.icon}"></div>
          <span class="data">${w.forecastDailyAverage.maxTemperature}&nbsp;&deg;C</span>
        </li>
      `)}
    </ul>
  </div>
`;

/**
 * Weather details desktop view
 * @param {Object} data
 * @return {TemplateResult}
 */
const weatherDetailsDesktop = (data: WeatherData): TemplateResult => {
  const current = data.details.forecasts[data.day];
  return html`
    <table class="header-weather">
      <tr>
        <td rowspan="3" class="current">
          <div class="max">
            <div title="${current.forecastDailyAverage.altText}"
                 class="icon-weather wi-${current.forecastDailyAverage.icon}"></div>
            <span class="label">Max.</span>
            <span class="data">${current.forecastDailyAverage.maxTemperature}&nbsp;&deg;C</span>
          </div>
        </td>
        <td class="data">${current.forecastDailyAverage.minTemperature}&nbsp;&deg;C</td>
        <td class="label">Min. Temp.</td>
      </tr>
      <tr>
        <td class="data">${current.forecastDailyAverage.precipitation}&nbsp;%</td>
        <td class="label">Niederschlag</td>
      </tr>
      <tr>
        <td class="data">${current.forecastDailyAverage.wind}&nbsp;km/h</td>
        <td class="label">Wind</td>
      </tr>
    </table>
    ${current.hasDetailedForecasts ? html`
      <table class="weather-desktop">
        <tr>
          <th></th>
          <th>Morgens</th>
          <th>Mittags</th>
          <th>Abends</th>
          <th>Nachts</th>
        </tr>
        <tr>
          <th><div class="label">Vorhersage</div></th>
          <td>
            <div title="${current.forecastMorning.altText}"
                 class="icon-weather wi-${current.forecastMorning.icon}"></div>
          </td>
          <td>
            <div title="${current.forecastNoon.altText}"
                 class="icon-weather wi-${current.forecastNoon.icon}"></div>
          </td>
          <td>
            <div title="${current.forecastEvening.altText}"
                 class="icon-weather wi-${current.forecastEvening.icon}"></div>
          </td>
          <td>
            <div title="${current.forecastNight.altText}"
                 class="icon-weather wi-night-${current.forecastNight.icon}"></div>
          </td>
        </tr>
        <tr>
          <th><div class="label">Temperatur</div></th>
          <td><span class="data">${current.forecastMorning.temperature}&nbsp;&deg;C</span></td>
          <td><span class="data">${current.forecastNoon.temperature}&nbsp;&deg;C</span></td>
          <td><span class="data">${current.forecastEvening.temperature}&nbsp;&deg;C</span></td>
          <td><span class="data">${current.forecastNight.temperature}&nbsp;&deg;C</span></td>
        </tr>
        <tr>
          <th><div class="label">Niederschlag</div></th>
          <td><span class="data">${current.forecastMorning.precipitation}&nbsp;%</span></td>
          <td><span class="data">${current.forecastNoon.precipitation}&nbsp;%</span></td>
          <td><span class="data">${current.forecastEvening.precipitation}&nbsp;%</span></td>
          <td><span class="data">${current.forecastNight.precipitation}&nbsp;%</span></td>
        </tr>
        <tr>
          <th><div class="label">Wind</div></th>
          <td>
            <span title="Aus ${current.forecastMorning.windDirection}"
                  class="icon icon-wind-${current.forecastMorning.windCode}"></span>
            <span class="data">${current.forecastMorning.wind}&nbsp;km/h</span>
          </td>
          <td>
            <span title="Aus ${current.forecastNoon.windDirection}"
                  class="icon icon-wind-${current.forecastNoon.windCode}"></span>
            <span class="data">${current.forecastNoon.wind}&nbsp;km/h</span>
          </td>
          <td>
            <span title="Aus ${current.forecastEvening.windDirection}"
                  class="icon icon-wind-${current.forecastEvening.windCode}"></span>
            <span class="data">${current.forecastEvening.wind}&nbsp;km/h</span>
          </td>
          <td>
            <span title="Aus ${current.forecastNight.windDirection}"
                  class="icon icon-wind-${current.forecastNight.windCode}"></span>
            <span class="data">${current.forecastNight.wind}&nbsp;km/h</span>
          </td>
        </tr>
      </table>
    ` : ''}
  `;
};

export {weatherPagerDetailsMobile, weatherDetailsMobile, weatherDetailsDesktop, weatherDetailsTab};
